<template>
	<div>
		<div class="stage">
			<page-header title="Feature Codes" />

			<filter-bar
				:show-filters="showFilters"
				:has-export="true"
				:show-add-button="$global.hasUserRole(userRoles, 'featurecodes:create')"
				add-button-label="Add Feature Code"
				@toggle-filters="showFilters = !showFilters"
				@reset-filters="resetSearchParams"
				@export="exportToCsv"
				@add-new="routeTo('/featurecodes/new')"
			>
			</filter-bar>

			<filter-section
				:show="showFilters"
				:filters="searchParams"
				:search-term="searchedFeatureCodes"
				:tenants="tenants"
				:accounts="accounts"
				:allow-tenants="true"
				tenant-label="Tenant Feature Codes"
				show-account-filter
				show-tenant-filter
				show-date-filter
				date-label="Date Added"
				show-search-filter
				search-label="Search"
				@update:filters="searchParams = $event"
				@update:search-term="searchedFeatureCodes = $event"
				@tenant-change="$global.setSelectedTenant"
			/>

			<data-view
				:view-mode="'table'"
				:columns="columns"
				:sort-column="sortColumn"
				:reverse="reverse"
				:loading="loading"
				:label="'Feature Codes'"
				:total-items="totalFeatureCodes"
				:page-size="pageSize"
				:current-page="currentPage"
				:pages="computedPages"
				@sort="setSort"
				@update:page-size="changePageSize"
				@page-change="changePage"
			>
				<template #table-view>
					<generic-table
						:items="featurecodes"
						:columns="columns"
						item-type="feature codes"
						base-route="featurecodes"
						:can-edit="$global.hasUserRole(userRoles, 'featurecodes:update')"
						:view-mode="'table'"
						:empty-state-config="{
							icon: 'fa-light fa-input-numeric',
							title: 'No feature codes yet',
							description: 'Create your first feature code to enable special dialing functions.',
							action: $global.hasUserRole(userRoles, 'featurecodes:create')
								? {
										label: 'Create Feature Code',
										icon: 'fa-light fa-plus',
										handler: () => routeTo('/featurecodes/new')
									}
								: null
						}"
						:actions="[
							{
								handler: (code) => routeTo(`/featurecodes/${code.id}`),
								icon: 'fa-light fa-pencil',
								label: 'Edit'
							},
							{
								permission: 'featurecodes:delete',
								handler: deleteFeatureCode,
								icon: 'fa-light fa-delete-left',
								label: 'Delete'
							}
						]"
					>
						<template #tenant="{ item }">
							{{ item.tenant?.companyName || 'Unassigned' }}
						</template>
						<template #account="{ item }">
							{{ item.account?.name || 'Unassigned' }}
						</template>
						<template #app="{ item }">
							{{ $global.prettyPrint(item.app) || 'None' }}
						</template>
						<template #appConfig="{ item }">
							{{ showConfig(item) || 'None' }}
						</template>
					</generic-table>
				</template>
			</data-view>
		</div>
	</div>
</template>

<script>
import featurecodesApi from '../scripts/featurecodes/featurecodes.api'
import accountsApi from '../scripts/accounts/accounts.api'
import { router } from '../router'
import axios from 'axios'
import moment from 'moment'
import debounce from 'lodash.debounce'

export default {
	inject: ['user', 'userRoles', 'tenants'],
	data() {
		return {
			columns: [
				{
					key: 'number',
					label: 'Number',
					sortable: true,
					class: 'col-xs-12 col-sm-3 col-md-2 col-lg-2'
				},
				{
					key: 'tenant',
					label: 'Tenant',
					class: 'col-xs-12 col-sm-2 col-md-2 col-lg-2'
				},
				{
					key: 'app',
					label: 'App',
					class: 'col-xs-12 col-sm-2 col-md-2 col-lg-2'
				},
				{
					key: 'appConfig',
					label: 'App Config',
					class: 'col-xs-12 col-sm-2 col-md-2 col-lg-2'
				},
				{
					key: 'account',
					label: 'Account',
					class: 'col-xs-12 col-sm-2 col-md-2 col-lg-2'
				},
				{
					key: 'createdAt',
					label: 'Date Added',
					sortable: true,
					class: 'col-xs-12 col-sm-2 col-md-2 col-lg-2 hideTablet'
				}
			],
			showFilters: true,
			searchParams: {
				dateAddedFrom: null,
				dateAddedTo: null,
				selectedTenant: '',
				selectedAccount: null
			},
			featurecodes: [],
			totalFeatureCodes: 0,
			accounts: [],
			pageSize: 25,
			currentPage: 1,
			sortColumn: 'number',
			reverse: false,
			loading: false,
			searchedFeatureCodes: '',
			files: [],
			sipTrunks: [],
			mobileSims: [],
			flows: []
		}
	},

	computed: {
		computedPages() {
			const numPages = Math.ceil(this.totalFeatureCodes / this.pageSize) || 1
			return Array.from({ length: numPages }, (_, i) => i)
		},
		selectedTenantName() {
			if (!this.searchParams.selectedTenant) return 'All Tenants'
			const tenant = this.tenants?.find((t) => t.id === parseInt(this.searchParams.selectedTenant))
			return tenant?.companyName || 'All Tenants'
		}
	},

	watch: {
		dates: {
			handler: function (val) {
				if (val?.length === 2) {
					this.searchParams.dateAddedFrom = moment(val[0]).format('YYYY-MM-DD')
					this.searchParams.dateAddedTo = moment(val[1]).format('YYYY-MM-DD')
					this.reloadFeatureCodes()
				}
			},
			deep: true
		},
		searchedFeatureCodes(...args) {
			this.debouncedWatch(...args)
		},
		searchParams: {
			handler(newValue, oldValue) {
				this.reloadFeatureCodes()
			},
			deep: true
		},
		currentPage: {
			handler(newValue, oldValue) {
				this.reloadFeatureCodes()
			}
		},
		'$route.query.globalSearch': {
			handler(newValue, oldValue) {
				if (newValue) this.reloadFeatureCodes(newValue)
			}
		}
	},

	created() {
		if (this.$global.getSelectedTenant()) this.searchParams.selectedTenant = this.$global.getSelectedTenant()

		accountsApi.getAll({ limit: 2500 }).then((response) => {
			this.accounts = response.data.results
		})

		this.debouncedWatch = debounce((newValue, oldValue) => {
			this.reloadFeatureCodes()
		}, 1000)
		if (this.$route.query && this.$route.query.globalSearch) this.reloadFeatureCodes(this.$route.query.globalSearch)
		else this.reloadFeatureCodes()
	},
	beforeUnmount() {
		this.debouncedWatch.cancel()
		if (this.getRefreshInterval) clearInterval(this.getRefreshInterval)
	},

	methods: {
		resetDates() {
			this.dates = []
			this.searchParams.dateAddedFrom = null
			this.searchParams.dateAddedTo = null
		},
		formatDates(date1, date2) {
			if (date1 && date2) return moment(date1).format('Do MMM YY') + ' to ' + moment(date2).format('Do MMM YY')
			else if (date1) return moment(date1).format('Do MMM YY')
			else return null
		},
		exportToCsv() {
			let searchParams = this.getSearchParams()

			searchParams.exportCsv = true

			axios
				.get(window.dtServerUrl + '/featurecodes', {
					params: searchParams
				})
				.then((response) => {
					let blob = new Blob([response.data], { type: response.headers.get('content-type') })
					// let filename = (response.headers['Content-Disposition'] || '').split('filename=')[1]
					let link = document.createElement('a')
					link.href = window.URL.createObjectURL(blob)
					link.download = 'teleflow-featurecodes.csv'
					link.click()
				})
				.catch((error) => {
					console.log(error)
				})
		},
		setSort(column, reverse) {
			if (this.sortColumn !== column) this.reverse = false
			else this.reverse = reverse

			this.sortColumn = column

			this.reloadFeatureCodes()
		},
		changePageSize(size) {
			this.pageSize = size
			let numPages = Math.ceil(this.totalSims / size) || 1
			if (numPages < this.currentPage) this.currentPage = numPages
			else this.reloadFeatureCodes()
		},
		toggleSelectAllSims() {
			this.allSelected = !this.allSelected

			for (let i = 0; i < this.featurecodes.length; i++) {
				this.featurecodes[i].selected = this.allSelected
			}
		},
		resetSearchParams() {
			this.searchParams = {
				dateAddedFrom: null,
				dateAddedTo: null,
				selectedTenant: ''
			}
		},
		getSearchParams() {
			let params = {
				limit: this.pageSize,
				skip: this.pageSize * (this.currentPage - 1),
				where: {}
			}

			if (this.searchParams) {
				params.where.createdAtFrom = this.searchParams.dateAddedFrom
					? moment(this.searchParams.dateAddedFrom).format('YYYY-MM-DD') + ' 00:00:00'
					: null
				params.where.createdAtTo = this.searchParams.dateAddedTo
					? moment(this.searchParams.dateAddedTo).format('YYYY-MM-DD') + ' 23:59:59'
					: this.searchParams.dateAddedFrom
						? moment(this.searchParams.dateAddedFrom).format('YYYY-MM-DD') + ' 23:59:59'
						: null
			}

			if (this.searchParams.selectedTenant) params.where.selectedTenant = this.searchParams.selectedTenant

			if (this.searchParams.selectedAccount) {
				params.where.selectedAccount = this.searchParams.selectedAccount
			}

			if (!this.sortColumn) this.sortColumn = 'number'

			params.order = {
				[this.sortColumn]: this.reverse ? 'DESC' : 'ASC'
			}

			return params
		},
		reloadFeatureCodes(globalSearch) {
			this.loading = true
			let params = this.getSearchParams()

			if (globalSearch) {
				params = { ...params, searchTerm: globalSearch }
			} else if (this.searchField) {
				params.searchTerm = this.searchField
			}

			featurecodesApi
				.getAll(params)
				.then((response) => {
					this.totalFeatureCodes = response.data.count
					this.featurecodes = response.data.results

					const maxPages = Math.ceil(this.totalFeatureCodes / this.pageSize) || 1
					if (this.currentPage > maxPages) {
						this.currentPage = 1
						this.reloadFeatureCodes(globalSearch)
						return
					}

					this.loading = false
				})
				.catch((err) => {
					console.error(err)
					this.loading = false
				})
		},
		deleteFeatureCode(code) {
			if (code) {
				this.$swal({
					title: 'Delete Feature Code',
					text: `Would you like to delete Feature Code: '${code.number}'?`,
					showCancelButton: true,
					confirmButtonText: 'Delete',
					cancelButtonText: 'Cancel',
					showDenyButton: false,
					denyButtonText: 'Cancel'
				}).then((result) => {
					if (result.isConfirmed)
						featurecodesApi
							.delete(code.id)
							.then((response) => {
								if (response && response.data && response.data.success) this.reloadFeatureCodes()
								else
									this.$swal({
										title: 'Error',
										icon: 'error',
										confirmButtonText: 'Confirm',
										text: response?.data?.error || 'Unknown error occurred, please contact support.'
									})
							})
							.catch(console.error)
				})
			}
		},
		routeTo(routeName) {
			router.push(routeName)
		},
		clearSelectedTenant() {
			this.searchParams.selectedTenant = ''
			this.$global.setSelectedTenant({ target: { value: '' } })
		},
		addFilterPill(name, category) {
			const pill = { name, category }
			this.pills.push(pill)
		},

		updatePills() {
			this.pills = []

			if (this.searchField) {
				this.addFilterPill(this.searchField, 'Name')
			}

			if (this.searchParams.selectedTenant) {
				this.addFilterPill(this.selectedTenantName, 'Tenant')
			}

			if (this.dates && this.dates.length) {
				this.addFilterPill(this.formatDates(this.dates[0], this.dates[1]), 'Date')
			}
		},

		removeFilterPill(pill) {
			const index = this.pills.findIndex((p) => p.name === pill.name && p.category === pill.category)
			if (index > -1) {
				this.pills.splice(index, 1)
			}

			switch (pill.category) {
				case 'Prefix':
					this.searchField = ''
					break
				case 'Tenant':
					this.searchParams.selectedTenant = ''
					break
				case 'Date':
					this.dates = []
					break
			}

			this.reloadAll()
		},
		changePage(page) {
			this.currentPage = page
			this.reloadFeatureCodes()
		},
		showConfig(featureCode) {
			if (featureCode.app && featureCode.appConfig && featureCode.appConfig[featureCode.app]) {
				const currentConfig = featureCode.appConfig[featureCode.app]

				switch (featureCode.app) {
					case 'speaking_clock':
					case 'diagnostics':
					case 'tenant_balance_check':
					case 'account_balance_check':
					case 'proxy':
						return ''
					case 'play_prompt': {
						let file = this.files.find((file) => file.id === currentConfig.fileId)
						return file ? file.name : ''
					}
					case 'dial_thru': {
						let file = this.files.find((file) => file.id === currentConfig.welcomePromptFileId)
						return file ? file.name : ''
					}
					case 'prompt_then_forward': {
						let file = this.files.find((file) => file.id === currentConfig.fileId)
						return file ? file.name : ''
					}
					case 'call_forwarding':
						return currentConfig.destination
					case 'sip_trunk': {
						let sipTrunk = this.sipTrunks.find(
							(sipTrunk) => sipTrunk.id === parseInt(currentConfig.trunkId)
						)
						return sipTrunk ? sipTrunk.username : ''
					}
					case 'mobile_sim': {
						let mobileSim = this.mobileSims.find(
							(mobileSim) => mobileSim.id === parseInt(currentConfig.mobileSimId)
						)
						return mobileSim ? mobileSim.number : ''
					}
					case 'flows': {
						let flow = this.flows.find((flow) => flow.id === parseInt(currentConfig.flowId))
						return flow ? flow.name : ''
					}
					default:
						return ''
				}
			}
			return ''
		}
	}
}
</script>
